import React from "react";
import { navigateTo } from "../utils/navigation";

export default function SupportPage(): React.JSX.Element {
  return (
    <div className="stack-7">

      {/* Page header */}
      <header className="stack-2">
        <h1 className="t-h2" style={{ margin: 0 }}>Support</h1>
        <p className="t-small t-mute" style={{ margin: 0 }}>We read every message.</p>
      </header>

      {/* Quick fixes */}
      <section className="stack-4">
        <h2 className="t-h3" style={{ margin: 0 }}>A few things to try first</h2>
        <div className="stack-2">
          {[
            "Allow camera and photo permissions for kivo.skin in your device settings.",
            "Update to the latest version of your browser or app.",
            "Try a hard refresh (Cmd/Ctrl + Shift + R) or open a private window.",
            "Consistent, natural lighting produces clearer appearance insights.",
          ].map((tip) => (
            <div key={tip} className="row-3" style={{ alignItems: "flex-start" }}>
              <span className="list-dot" />
              <span className="t-small">{tip}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Contact & actions */}
      <section className="stack-4">
        <h2 className="t-h3" style={{ margin: 0 }}>Get in touch</h2>
        <div className="g-2">

          <div className="card card--sand card--flat stack-4">
            <div className="stack-2">
              <h3 style={{ font: "500 var(--fs-body)/1.3 var(--f-display)", margin: 0 }}>Contact us</h3>
              <p className="t-small t-mute" style={{ margin: 0 }}>
                Typical reply time: 1–2 business days.
              </p>
            </div>
            <div className="stack-2">
              <a
                className="btn btn--primary btn--full"
                href="mailto:support@kivo.skin?subject=Support%20Request&body=Describe%20what%20happened%20(and%20attach%20a%20screenshot%20if%20you%20can)."
              >
                Email support@kivo.skin
              </a>
              <button
                onClick={() => { navigateTo("/history"); }}
                className="btn btn--ghost btn--full"
              >
                Check my appearance insights
              </button>
            </div>
          </div>

          <div className="card card--sand card--flat stack-4">
            <div className="stack-2">
              <h3 style={{ font: "500 var(--fs-body)/1.3 var(--f-display)", margin: 0 }}>Billing & plan</h3>
              <p className="t-small t-mute" style={{ margin: 0 }}>
                Questions about upgrades, charges, or receipts?
              </p>
            </div>
            <div className="stack-2">
              <button
                onClick={() => { navigateTo("/plans"); }}
                className="btn btn--ghost btn--full"
              >
                Manage my plan
              </button>
              <a
                className="btn btn--ghost btn--full"
                href="mailto:support@kivo.skin?subject=Billing%20Question&body=Please%20include%20your%20account%20email%20and%20details."
              >
                Ask about billing
              </a>
            </div>
          </div>

          <div className="card card--sand card--flat stack-4">
            <div className="stack-2">
              <h3 style={{ font: "500 var(--fs-body)/1.3 var(--f-display)", margin: 0 }}>Data & privacy</h3>
              <p className="t-small t-mute" style={{ margin: 0 }}>
                Access, delete, or manage how your content is used.
              </p>
            </div>
            <div className="stack-2">
              <a
                className="btn btn--ghost btn--full"
                href="mailto:support@kivo.skin?subject=Data%20Request%20(Access%20or%20Deletion)&body=Please%20state%20your%20request%20and%20confirm%20your%20account%20email."
              >
                Request my data or deletion
              </a>
              <button
                onClick={() => { navigateTo("/privacy"); }}
                className="btn btn--ghost btn--full"
              >
                Read Privacy Policy
              </button>
            </div>
          </div>

          <div className="card card--sand card--flat stack-4">
            <div className="stack-2">
              <h3 style={{ font: "500 var(--fs-body)/1.3 var(--f-display)", margin: 0 }}>Bug reports</h3>
              <p className="t-small t-mute" style={{ margin: 0 }}>
                Found something off? Let us know.
              </p>
            </div>
            <a
              className="btn btn--ghost btn--full"
              href="mailto:support@kivo.skin?subject=Bug%20Report&body=Steps%20to%20reproduce%3A%0AExpected%3A%0AActual%3A%0AEnvironment%20(browser%2FOS)%3A"
            >
              Send a bug report
            </a>
          </div>

        </div>
      </section>

      {/* Micro-break — lighthearted, brand-gentle */}
      <section className="card card--sand card--flat stack-4">
        <div className="stack-2">
          <h3 className="t-h3" style={{ margin: 0 }}>Take a moment</h3>
          <p className="t-small t-mute" style={{ margin: 0 }}>
            While we look into things, three minutes of slow jazz tends to help.
          </p>
        </div>
        <div className="row-4">
          <a
            className="btn btn--primary"
            href="https://open.spotify.com/search/jazz"
            target="_blank"
            rel="noopener noreferrer"
          >
            Play some jazz
          </a>
          <button onClick={() => { navigateTo("/"); }} className="btn btn--ghost">
            Back to home
          </button>
        </div>
      </section>

      {/* Legal & compliance */}
      <section className="stack-4">
        <h2 className="t-label" style={{ margin: 0 }}>Legal & Compliance</h2>
        <div className="stack-3">
          <div className="row-3" style={{ alignItems: "flex-start" }}>
            <span className="list-dot" />
            <p className="t-small" style={{ margin: 0 }}>
              Read our{" "}
              <button onClick={() => { navigateTo("/terms"); }} className="link">Terms of Service</button>
              ,{" "}
              <button onClick={() => { navigateTo("/privacy"); }} className="link">Privacy Policy</button>
              ,{" "}
              <button onClick={() => { navigateTo("/ai-disclaimer"); }} className="link">AI Disclaimer</button>
              , and{" "}
              <button onClick={() => { navigateTo("/photo-consent"); }} className="link">Biometric Consent</button>
              .
            </p>
          </div>
          <div className="row-3" style={{ alignItems: "flex-start" }}>
            <span className="list-dot" />
            <p className="t-small" style={{ margin: 0 }}>
              EU/UK residents can exercise GDPR rights via the Data & privacy section above or by
              emailing{" "}
              <a href="mailto:support@kivo.skin" className="link">support@kivo.skin</a>
              .
            </p>
          </div>
          <div className="row-3" style={{ alignItems: "flex-start" }}>
            <span className="list-dot" />
            <p className="t-small" style={{ margin: 0 }}>
              Security issues? Email with subject &quot;Security&quot; and we&apos;ll prioritize it.
            </p>
          </div>
        </div>
      </section>

    </div>
  );
}
